import { useRef, useState } from 'react';
import { Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';

type ScheduledJobModalFooterProps = {
  job: ScheduledJobItem | null;
  canSave: boolean;
  isSaving: boolean;
  onSave: () => void;
  onOpenChat: () => void;
  /** When given, Delete shows; it asks for confirmation first. Rejects on failure. */
  onDelete?: (job: ScheduledJobItem) => Promise<void>;
  /** Called after a successful delete so the dialog can close. */
  onDeleted: () => void;
};

const footerButtonClass =
  'h-12 w-full rounded-full px-6 text-sm font-semibold leading-heading shadow-none';

/** Save, Open Chat and Delete (with confirmation) for the routine dialog. */
export function ScheduledJobModalFooter({
  job,
  canSave,
  isSaving,
  onSave,
  onOpenChat,
  onDelete,
  onDeleted,
}: Readonly<ScheduledJobModalFooterProps>) {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // A ref, not state: a fast double click on Confirm must send one request.
  const deleteStarted = useRef(false);

  const handleConfirmDelete = async () => {
    if (!job || !onDelete || deleteStarted.current) return;
    deleteStarted.current = true;
    setIsDeleting(true);
    try {
      await onDelete(job);
      setIsConfirmOpen(false);
      onDeleted();
    } catch {
      // The delete handler already showed the error toast; keep the dialog open.
      setIsConfirmOpen(false);
    } finally {
      deleteStarted.current = false;
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex w-full shrink-0 flex-col gap-3 px-8 pb-8 pt-6 sm:flex-row">
      <Button
        type="button"
        variant="outline"
        className={`${footerButtonClass} border-grey-100 text-grey-900 dark:border-border dark:text-foreground sm:flex-1`}
        onClick={onSave}
        disabled={!canSave}
        loading={isSaving}
      >
        {t('SAVE', { defaultValue: 'Save' })}
      </Button>
      <Button
        type="button"
        className={`${footerButtonClass} bg-black-base text-true-white hover:bg-grey-900 dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white sm:flex-1`}
        onClick={onOpenChat}
        disabled={!job}
      >
        {t('OPEN_CHAT', { defaultValue: 'Open Chat' })}
      </Button>
      {onDelete && (
        <Button
          type="button"
          variant="outline"
          className={`${footerButtonClass} gap-2 border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive dark:border-error-background dark:text-error dark:hover:border-error dark:hover:bg-error-background dark:hover:text-error sm:order-first sm:w-auto`}
          onClick={() => setIsConfirmOpen(true)}
          disabled={!job}
        >
          <Trash width={18} height={18} strokeWidth={1.5} aria-hidden />
          {t('DELETE', { defaultValue: 'Delete' })}
        </Button>
      )}
      <ConfirmationModal
        nested
        open={isConfirmOpen}
        onOpenChange={(open) => {
          if (!isDeleting) setIsConfirmOpen(open);
        }}
        title={t('SCHEDULED_JOB_DELETE_CONFIRM_TITLE', { defaultValue: 'Delete routine?' })}
        description={t('SCHEDULED_JOB_DELETE_CONFIRM_DESCRIPTION', {
          defaultValue: 'The routine stops running. Its run history is kept.',
        })}
        confirmText={t('DELETE', { defaultValue: 'Delete' })}
        cancelText={t('CANCEL', { defaultValue: 'Cancel' })}
        confirmVariant="delete"
        preventAutoClose
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
